/**
 * Workflow module registry — the single source of truth for module
 * types. Both the admin-ui editor (React Flow palette + properties
 * panel auto-form) AND the Lambda executor import from here. Adding a
 * future module type is one PR in one file:
 *   1. Append to `MODULE_REGISTRY` below with config schema + ports +
 *      default config.
 *   2. Implement the handler in `packages/admin-api/src/lib/workflow-modules/<id>.ts`.
 *
 * Module ids are namespaced by kind: `trigger.X`, `data.X`, `action.X`,
 * `logic.X`. The kind drives the editor sidebar grouping.
 */
import { z } from 'zod';
import { FAN_EVENT_TYPES } from './workflow.js';
/**
 * Shared LLM-options block. Embedded in any workflow module that calls
 * an LLM. Operator-facing toggles + numbers that the executor reads at
 * runtime to decide what to inject into the prompt and which API key
 * to use. See `workflow-llm-options` OpenSpec capability for the full
 * contract (persona block format, recent-context query semantics, etc).
 */
export const LlmOptionsSchema = z.object({
    /** When true, executor prepends a 「角色設定」 block built from
     *  character.personality + ai.persona.* into the user message. */
    includePersona: z.boolean().default(false),
    /** When true, executor queries fan-conversation-turns and prepends
     *  a 「近期對話摘要」 block (last N hours, top M turns). */
    includeRecentContext: z.boolean().default(false),
    /** Hours of history to consider when includeRecentContext=true. */
    recentContextHours: z.number().int().min(1).max(168).default(24),
    /** Max turns kept when includeRecentContext=true. */
    recentContextMaxTurns: z.number().int().min(1).max(100).default(20),
    /** Which API key the executor uses for the LLM call:
     *   - 'character': character.ai.apiKey (existing behaviour, default)
     *   - 'platform':  platform LLM config singleton (see
     *                  platform-llm-provider capability) */
    apiKeySource: z.enum(['character', 'platform']).default('character'),
});
// ── Trigger modules ────────────────────────────────────────────────
const ScheduleConfig = z.object({
    /** Standard 5-field cron. The editor's wizard emits this; advanced
     *  users may type it raw. Validation here is loose — full parsing
     *  happens server-side via the AWS Scheduler API rejection. */
    cron: z.string().min(9).max(120),
    /** UTC offset hours used by the wizard to render the operator-local
     *  preview (e.g. "每天 08:00 (台北)" = "0 0 * * *" UTC).
     *  -12..14 covers all real-world offsets including India's +5:30
     *  (we store as decimal; +5.5). */
    tzOffsetHours: z.number().min(-12).max(14).default(0),
});
const FanEventConfig = z.object({
    eventType: z.enum(FAN_EVENT_TYPES),
    /** For inactive-days, the threshold. Ignored for other event types. */
    inactiveDays: z.number().int().min(1).max(365).optional(),
});
const ManualTriggerConfig = z.object({
    /** Operator-supplied note shown next to the Run-now button. */
    note: z.string().max(200).default(''),
});
// ── Data fetcher modules ───────────────────────────────────────────
const FetchFansConfig = z.object({
    groupId: z.string().uuid().optional(),
    minTier: z.number().int().min(1).max(10).optional(),
    maxTier: z.number().int().min(1).max(10).optional(),
    /** Fan rows whose lastInteractionAt is > N days ago. Useful for
     *  win-back / decay flows. */
    inactiveDays: z.number().int().min(1).max(365).optional(),
    /** Cap result size for safety. Default 200; max 1000. */
    limit: z.number().int().min(1).max(1000).default(200),
});
// Production fan ids are usually Cognito UUIDs, while local development
// accounts use stable readable ids. Both are valid workflow targets.
const FanIdSchema = z.string().trim().min(1).max(128);
const FetchFanConfig = z.object({
    /** Static fan id; if omitted, the node reads the `in` port value
     *  (so upstream can pass a fanId dynamically). */
    fanId: FanIdSchema.optional(),
});
const FetchCharacterLifeConfig = z.object({});
// ── Action modules ────────────────────────────────────────────────
const LifeTickConfig = z.object({
    /** life-tick keeps its historical behaviour by default — both
     *  injections on, character key. Operators can opt OUT explicitly. */
    llmOptions: LlmOptionsSchema.default({
        includePersona: true,
        includeRecentContext: true,
        recentContextHours: 24,
        recentContextMaxTurns: 20,
        apiKeySource: 'character',
    }),
});
const SendMessageToFanConfig = z.object({
    /** Message body. Templating with {{fan.displayName}} etc. arrives
     *  in Phase 3; v1 is plain text. */
    text: z.string().min(1).max(2000),
    /** Static fan id; if omitted, the node reads from the `in` port. */
    fanId: FanIdSchema.optional(),
});
const DecayAffinityConfig = z.object({
    /** Negative integer; clamped server-side to [-10, -1]. */
    delta: z.number().int().min(-10).max(-1).default(-1),
});
const HttpRequestConfig = z.object({
    url: z.string().url(),
    method: z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']).default('GET'),
    headers: z.record(z.string(), z.string()).default({}),
    /** Plain JSON object (will be stringified) or a string (sent as-is). */
    body: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
    /** Hard timeout ms. Capped server-side at 30000. */
    timeoutMs: z.number().int().min(1000).max(30_000).default(10_000),
});
const LlmGenerateConfig = z.object({
    /** System prompt. */
    systemPrompt: z.string().min(0).max(8000).default(''),
    /** User prompt. */
    userPrompt: z.string().min(1).max(8000),
    maxTokens: z.number().int().min(1).max(4000).default(400),
    /** When set, uses this OpenAI-compatible model id. Otherwise the
     *  character's own configured model is used (or platform default
     *  when apiKeySource=platform). */
    model: z.string().min(1).max(120).optional(),
    /** Per the workflow-llm-options capability — defaults all-off for
     *  llm-generate (operators write their own prompts and choose
     *  exactly what to include). */
    llmOptions: LlmOptionsSchema.default({}),
});
const LineNotifyConfig = z.object({
    /** LINE Notify token. Empty allowed at save time (operator may save
     *  a partially-configured node); executor rejects empty at run time
     *  with an actionable error. Stored encrypted; never returned to UI
     *  after save. */
    token: z.string().max(120).default(''),
    /** Message body. Same templating caveats as send-message-to-fan. */
    text: z.string().min(1).max(1000),
});
const DiscordWebhookConfig = z.object({
    /** Webhook URL. Empty allowed at save (placeholder) but executor
     *  rejects empty / non-URL at run time. Stored encrypted; never
     *  returned to UI after save. */
    webhookUrl: z.string().max(500).default(''),
    /** Message body (Discord-compatible markdown). */
    content: z.string().min(1).max(2000),
    /** Optional username override (Discord standard). */
    username: z.string().max(80).optional(),
});
// ── Logic modules ─────────────────────────────────────────────────
const BranchConfig = z.object({
    /** Sandboxed expression evaluated against `value` (the input). The
     *  expression evaluator only supports: literals (number, string,
     *  null, true, false), property access on `value`, comparison
     *  operators (== != < <= > >=), logical operators (&& || !).
     *  Anything else throws at save AND execute. */
    expression: z.string().min(1).max(400),
});
const ForEachConfig = z.object({
    /** v1 schema-only — placeholder for Phase 3. The editor accepts the
     *  node but executor throws "not implemented" if downstream is
     *  connected. */
    reserved: z.literal(true).default(true),
});
const DelayConfig = z.object({
    /** Capped at 900 s = 15 min (Lambda max). */
    seconds: z.number().int().min(1).max(900),
});
// ── Registry ──────────────────────────────────────────────────────
export const MODULE_REGISTRY = {
    // ── Triggers ────────────────────────────────────────────────
    'trigger.schedule': {
        kind: 'trigger',
        label: '⏰ 排程',
        icon: '⏰',
        description: '按 cron 排程定期觸發。',
        inputPorts: [],
        outputPorts: ['out'],
        configSchema: ScheduleConfig,
        defaultConfig: { cron: '0 9 * * *', tzOffsetHours: 8 },
    },
    'trigger.fan-event': {
        kind: 'trigger',
        label: '👤 粉絲事件',
        icon: '👤',
        description: '指定粉絲事件的測試條件；目前可由後台「立刻執行」測試，尚未連接正式事件自動觸發。',
        inputPorts: [],
        outputPorts: ['out'],
        configSchema: FanEventConfig,
        defaultConfig: { eventType: 'tier-up' },
    },
    'trigger.manual': {
        kind: 'trigger',
        label: '🖱 手動',
        icon: '🖱',
        description: '只能透過後台「立刻執行」按鈕觸發。',
        inputPorts: [],
        outputPorts: ['out'],
        configSchema: ManualTriggerConfig,
        defaultConfig: { note: '' },
    },
    // ── Data ────────────────────────────────────────────────────
    'data.fetch-fans': {
        kind: 'data',
        label: '📋 取得粉絲清單',
        icon: '📋',
        description: '列出符合條件的粉絲（群組、tier、不活躍天數等）。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: FetchFansConfig,
        defaultConfig: { limit: 200 },
    },
    'data.fetch-fan': {
        kind: 'data',
        label: '👤 取得單一粉絲',
        icon: '👤',
        description: '依 fanId 撈一位粉絲（id 可寫死或由上游傳入）。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: FetchFanConfig,
        defaultConfig: {},
    },
    'data.fetch-character-life': {
        kind: 'data',
        label: '🌅 取得角色狀態',
        icon: '🌅',
        description: '讀取此角色當前的 mood / scene / recentEvents。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: FetchCharacterLifeConfig,
        defaultConfig: {},
    },
    // ── Actions ────────────────────────────────────────────────
    'action.life-tick': {
        kind: 'action',
        label: '🌅 更新心情',
        icon: '🌅',
        description: '跑一次 life-tick — 用角色當前狀態 + 互動次數，LLM 生成新心情/場景/事件。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: LifeTickConfig,
        // Empty defaultConfig — zod's .default() on llmOptions kicks in at parse time
        defaultConfig: {},
    },
    'action.send-message-to-fan': {
        kind: 'action',
        label: '💬 發送訊息',
        icon: '💬',
        description: '推播一則訊息給粉絲（粉絲端會看到角色傳訊）。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: SendMessageToFanConfig,
        defaultConfig: { text: '嗨，我想到你了。' },
    },
    'action.decay-affinity': {
        kind: 'action',
        label: '📉 好感度衰減',
        icon: '📉',
        description: '對上游傳入的粉絲清單，每位減少指定的 affinity 分數。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: DecayAffinityConfig,
        defaultConfig: { delta: -1 },
    },
    'action.http-request': {
        kind: 'action',
        label: '🌐 HTTP 請求',
        icon: '🌐',
        description: '呼叫任意 REST API，回應 body 以 JSON 形式輸出到下游。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: HttpRequestConfig,
        defaultConfig: { url: 'https://example.com', method: 'GET', headers: {}, timeoutMs: 10_000 },
    },
    'action.llm-generate': {
        kind: 'action',
        label: '🤖 LLM 生成',
        icon: '🤖',
        description: '用角色自己的 OpenAI API 金鑰跑一個 prompt，結果輸出到下游。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: LlmGenerateConfig,
        // llmOptions auto-fills via zod default.
        defaultConfig: { systemPrompt: '', userPrompt: '請用一句話形容今天的天氣。', maxTokens: 400 },
    },
    'action.line-notify': {
        kind: 'action',
        label: '💌 LINE Notify',
        icon: '💌',
        description: '透過 LINE Notify 推送通知（需要 token）。',
        inputPorts: ['in'],
        outputPorts: [],
        configSchema: LineNotifyConfig,
        defaultConfig: { token: '', text: '提醒：…' },
    },
    'action.discord-webhook': {
        kind: 'action',
        label: '🎮 Discord Webhook',
        icon: '🎮',
        description: '送訊息到 Discord 頻道（透過 webhook URL）。',
        inputPorts: ['in'],
        outputPorts: [],
        configSchema: DiscordWebhookConfig,
        defaultConfig: { webhookUrl: '', content: '提醒：…' },
    },
    // ── Logic ──────────────────────────────────────────────────
    'logic.branch': {
        kind: 'logic',
        label: '❓ 條件分支',
        icon: '❓',
        description: '依據條件運算決定走 true / false 哪條線。',
        inputPorts: ['in'],
        outputPorts: ['true', 'false'],
        configSchema: BranchConfig,
        defaultConfig: { expression: 'value.tier >= 6' },
    },
    'logic.for-each': {
        kind: 'logic',
        label: '🔁 對每位粉絲',
        icon: '🔁',
        description: '對上游清單中每一項，跑一次下游節點（v1 schema only — Phase 3 才能真的執行）。',
        inputPorts: ['in'],
        outputPorts: ['each'],
        configSchema: ForEachConfig,
        defaultConfig: { reserved: true },
    },
    'logic.delay': {
        kind: 'logic',
        label: '⏸ 延遲',
        icon: '⏸',
        description: '暫停指定秒數後再執行下游（最多 900 秒 = 15 分鐘）。',
        inputPorts: ['in'],
        outputPorts: ['out'],
        configSchema: DelayConfig,
        defaultConfig: { seconds: 60 },
    },
};
/** Set of valid trigger module ids — used by `validateDag` to count
 *  triggers without depending on a string prefix convention. */
export const TRIGGER_MODULE_IDS = new Set(Object.entries(MODULE_REGISTRY)
    .filter(([, spec]) => spec.kind === 'trigger')
    .map(([id]) => id));
/** All known module ids; used by REST validation to reject typos. */
export const ALL_MODULE_IDS = new Set(Object.keys(MODULE_REGISTRY));
/**
 * Validate a node's `config` against its module's schema. Returns a
 * tuple [ok, error]. Server-side validation pathway — the editor
 * surfaces these inline on the offending node before allowing save.
 */
export function validateNodeConfig(type, config) {
    const spec = MODULE_REGISTRY[type];
    if (!spec)
        return { ok: false, message: `unknown module type: ${type}` };
    const result = spec.configSchema.safeParse(config);
    if (!result.success) {
        const issue = result.error.issues[0];
        return { ok: false, message: issue ? `${issue.path.join('.')}: ${issue.message}` : 'invalid config' };
    }
    return { ok: true, data: result.data };
}
/** Validate the source/target port ids of an edge against the referenced
 *  nodes' modules. Returns null if OK, or an error message. */
export function validateEdgePorts(edge, nodeById) {
    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);
    if (!sourceNode || !targetNode)
        return null; // dangling — caught elsewhere
    const sourceSpec = MODULE_REGISTRY[sourceNode.type];
    const targetSpec = MODULE_REGISTRY[targetNode.type];
    if (!sourceSpec || !targetSpec)
        return null;
    if (!sourceSpec.outputPorts.includes(edge.sourcePort)) {
        return `source node ${edge.source} (${sourceNode.type}) has no output port '${edge.sourcePort}'`;
    }
    if (!targetSpec.inputPorts.includes(edge.targetPort)) {
        return `target node ${edge.target} (${targetNode.type}) has no input port '${edge.targetPort}'`;
    }
    return null;
}
